import React, { useState, useEffect, useMemo } from 'react';
import { Radio, MapPin, X, Save, Navigation, Search, Filter, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import './RSUManagement.css';

const RSUManagement = () => {
  // --- States สำหรับข้อมูลหลัก ---
  const [rsuList, setRsuList] = useState(() => {
    const saved = localStorage.getItem('RSU_POINTS');
    return saved ? JSON.parse(saved) : [
      { id: 'RSU-001', name: 'อาจณรงค์ (S1)', lat: 13.7011, lon: 100.5913 },
      { id: 'RSU-002', name: 'พระราม 9 (S2)', lat: 13.7540, lon: 100.5977 },
      { id: 'RSU-003', name: 'ลาดพร้าว (S3)', lat: 13.7915, lon: 100.6105 },
      { id: 'RSU-004', name: 'วัชรพล (S4)', lat: 13.8455, lon: 100.6385 }
    ];
  });

  // --- States สำหรับ UI Control ---
  const [isAdding, setIsAdding] = useState(false);
  const [newRSU, setNewRSU] = useState({ id: '', name: '', lat: '', lon: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- Pagination States ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // บันทึกลง localStorage ทุกครั้งที่ rsuList เปลี่ยนแปลง (ซิงค์ไปหน้า Map)
  useEffect(() => {
    localStorage.setItem('RSU_POINTS', JSON.stringify(rsuList));
  }, [rsuList]);

  // --- Filter Logic ---
  const filteredRSUs = useMemo(() => {
    return rsuList.filter(rsu => 
      rsu.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rsu.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [rsuList, searchTerm]);

  // เมื่อค้นหา ให้กลับไปหน้า 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredRSUs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRSUs.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSave = () => {
    if (!newRSU.name || !newRSU.lat || !newRSU.lon) return alert("กรุณากรอกข้อมูลให้ครบ");
    
    const updated = [...rsuList, { 
      ...newRSU, 
      id: `RSU-00${rsuList.length + 1}`,
      lat: parseFloat(newRSU.lat),
      lon: parseFloat(newRSU.lon)
    }];
    
    setRsuList(updated);
    setIsAdding(false);
    setNewRSU({ id: '', name: '', lat: '', lon: '' });
  };

  const deleteRSU = (id) => {
    if (window.confirm("ลบจุดติดตั้งนี้? ข้อมูลหมุดบนแผนที่จะหายไปด้วย")) {
      setRsuList(rsuList.filter(r => r.id !== id));
    }
  };

  return (
    <div className="rsu-container">
      {/* ส่วนหัวและตัวกรองข้อมูล */}
      <div className="rsu-header">
        <div>
          <h2>RSU Management</h2>
          <p className="subtitle">จัดการจุดติดตั้งอุปกรณ์ Road Side Unit ({rsuList.length} สถานี)</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <Search size={18} />
            <input 
              type="text" 
              placeholder="ค้นหาชื่อสถานี หรือ ID..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn-add" onClick={() => setIsAdding(true)}>+ เพิ่มจุดติดตั้ง</button>
        </div>
      </div>

      {/* Modal เพิ่มข้อมูล */}
      {isAdding && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3><Navigation size={18}/> ลงทะเบียน RSU ใหม่</h3>
              <X onClick={() => setIsAdding(false)} style={{cursor:'pointer'}}/>
            </div>
            <div className="form-grid">
              <div className="input-field">
                <label>ชื่อสถานี</label>
                <input value={newRSU.name} onChange={e => setNewRSU({...newRSU, name: e.target.value})} placeholder="เช่น แยกลาดพร้าว" />
              </div>
              <div className="input-field">
                <label>Latitude</label>
                <input type="number" step="any" value={newRSU.lat} onChange={e => setNewRSU({...newRSU, lat: e.target.value})} placeholder="13.xxxx" />
              </div>
              <div className="input-field">
                <label>Longitude</label>
                <input type="number" step="any" value={newRSU.lon} onChange={e => setNewRSU({...newRSU, lon: e.target.value})} placeholder="100.xxxx" />
              </div>
            </div>
            <button className="btn-save" style={{marginTop:'20px', width:'100%'}} onClick={handleSave}>
              <Save size={16}/> บันทึกจุดติดตั้ง
            </button>
          </div>
        </div>
      )}

      {/* รายการ RSU */}
      <div className="rsu-list">
        {currentItems.length > 0 ? currentItems.map(rsu => (
          <div key={rsu.id} className="rsu-card">
            <div className="rsu-row-main">
              <div className="icon-circle">
                <Radio size={24} color="#6a1b9a" />
              </div>
              <div style={{marginLeft:'15px', flex: 1}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <h4>{rsu.name}</h4>
                  <span className="id-badge">{rsu.id}</span>
                </div>
                <small className="coord-text"><MapPin size={12}/> {rsu.lat.toFixed(6)}, {rsu.lon.toFixed(6)}</small>
              </div>
              <button 
                className="btn-delete-item"
                onClick={() => deleteRSU(rsu.id)} 
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        )) : (
          <div className="no-data">ไม่พบข้อมูลจุดติดตั้งที่ค้นหา</div>
        )}
      </div>

      {/* แถบควบคุม Pagination */}
      {totalPages > 1 && (
        <div className="pagination-bar">
          <button 
            className="page-btn" 
            disabled={currentPage === 1} 
            onClick={() => paginate(currentPage - 1)}
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="page-info">
            หน้า <span>{currentPage}</span> จาก {totalPages}
          </div>

          <button 
            className="page-btn" 
            disabled={currentPage === totalPages} 
            onClick={() => paginate(currentPage + 1)}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default RSUManagement;